import { Component, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subject, zip } from 'rxjs';
import { catchError, debounceTime, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { CommunicationsApiService } from 'src/app/modules/management/store/communication-center/communications-api.service';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import {
  ChannelType, MassCommsContact, MassCommsContactDataset,
} from 'src/app/modules/management/store/communication-center';

@Component({
    selector: 'topo-contact-select',
    templateUrl: './contact-select.component.html',
    styleUrls: ['./contact-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: ContactSelectComponent
        }
    ],
    standalone: false
})
export class ContactSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder: string;
  @Input() selectedRecipients: any[];
  @Input() channelType: ChannelType;
  @Input() channelUId: string;
  @Input() datasets: MassCommsContactDataset[];
  @Output() selectedRecipientsChanged = new EventEmitter<any[]>();

  recipientsInput$ = new Subject<string>();
  destroy$: Subject<void> = new Subject<void>();
  contacts$ = new BehaviorSubject<any[]>([]);
  dist$ = new BehaviorSubject<any[]>([]);
  distListGroupName: string;
  contactGroupName: string;
  recipientsLoading: boolean;
  recipients: any[];

  touched = false;
  @Input() disabled = false;
  onChange = value => {};
  onTouched = () => {};

  constructor(
    private store: Store<fromAuth.State>,
    private translate: TranslateService,
    private communicationsApiService: CommunicationsApiService
  ) {}

  ngOnInit(): void {
    this.translate.get(['MAP.DISTRIBUTION-LISTS', 'MAP.CONTACTS']).subscribe(values => {
      this.distListGroupName = values['MAP.DISTRIBUTION-LISTS'];
      this.contactGroupName = values['MAP.CONTACTS'];
    });

    zip(this.contacts$, this.dist$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(([contacts, dists]) => {
        this.recipientsLoading = false;

        const values = [];

        values.push(
          ...contacts.map(u => {
            return {
              contactId: u.contactId,
              displayName: u.name,
              type: this.contactGroupName
            };
          })
        );

        values.push(
          ...dists.map(d => {
            return {
              uId: d.uId,
              displayName: d.name,
              type: this.distListGroupName
            };
          })
        );

        this.recipients = values;
      });

    this.recipientsInput$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(val => {
      this.searchForContactsAndDistributionLists(val);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  searchForContactsAndDistributionLists(searchTerm: string): void {
    this.getContacts(searchTerm)
        .pipe(take(1))
        .subscribe(contact => {
          this.contacts$.next(contact);
        });

    this.getDistributionLists(searchTerm)
      .pipe(take(1))
      .subscribe(dist => {
        this.dist$.next(dist);
      });
  }

  getContacts(searchTerm: string = null): Observable<MassCommsContact[]> {
    this.recipientsLoading = true;

    return this.communicationsApiService
      .getMassCommsContactsFromDatasets(
        this.datasets,
        0,
        20,
        searchTerm
      )
      .pipe(
        catchError(e => {
          console.error(e);
          return of({ data: [] });
        }),
        map(({ data }) => {
          return data;
        })
      );
  }

  getDistributionLists(searchTerm: string = null): Observable<any[]> {
    this.recipientsLoading = true;

    let where = `ChannelType==ChannelType.${ChannelType[this.channelType]} && ChannelUId==Guid("${this.channelUId}")`;
    where += !!searchTerm ? ` && Name.ToLower().Contains("${searchTerm.replace('"', '\\"')?.toLocaleLowerCase()}")` : '';

    return this.communicationsApiService
      .getDistributionLists({
        skip: 0,
        take: 20,
        sort: 'Name',
        where
      })
      .pipe(
        catchError(e => {
          console.error(e);
          return of({ data: [] });
        }),
        map(({ data }) => {
          return data;
        })
      );
  }

  addRecipient(event): void {
    this.selectedRecipientsChanged.emit(this.selectedRecipients);
    this.markAsTouched();
    this.onChange(this.selectedRecipients);
  }

  removeRecipient(event): void {
    this.selectedRecipientsChanged.emit(this.selectedRecipients);
    this.markAsTouched();
    this.onChange(this.selectedRecipients);
  }

  writeValue(selectedRecipients: any[]) {
    this.selectedRecipients = selectedRecipients;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  trackByFn(val) {
    return val;
  }
}
